import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, ViewEncapsulation } from '@angular/core';
import { format } from 'date-fns';
import { AppInfo } from '../../../../../../../global/environment';
import { FeatureFlags } from '../../../../../../../global/featureFlags/featureFlags';
import { Tracking } from '../../../../../../../global/tracking/tracking';
import { ButtonComponent } from '../../../../../../../shared/components/button/button.component';
import { MaintenanceBannerComponent } from '../../../../../../../shared/components/maintenance-banner/maintenance-banner.component';
import { TelephoneLinkComponent } from '../../../../../../../shared/components/telephone-link/telephone-link.component';
import { QMBlockDirective, QMEncryptDirective } from '../../../../../../../shared/directives/quantum-metric.directive';
import { FirstLetterCapitalisePipe } from '../../../../../../../shared/pipes/firstLetterCapitilise';
import { TimeFormatPipe } from '../../../../../../../shared/pipes/time-format.pipe';
import { downloadPDF } from '../../../../../../../shared/utils/download-pdf';
import { capitaliseText } from '../../../../../../../shared/utils/formatting';
import { ORBIT_BUNDLE, ORBIT_BUNDLE_SESSION_KEY } from '../../../../../constants';
import { ConfirmationPageType, ExcessClaimsMessage, GAService } from '../../../../services/ga-tags.service';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { ContactHoursComponent } from '../../../shared/contact-hours/contact-hours.component';
import { CustomRoundPipe } from '../payment-form/payment-form.component';
import { ManualSignpostingComponent } from './manual-signposting/manual-signposting.component';
import { floorcareBannerMessage, FloorcareProviders } from './utils/floorcare-helper';
import { VulnerableCustomersComponent } from './vulnerable-customers/vulnerable-customers.component';

export type Appointment = {
  allDay?: boolean;
  date?: string;
  endTime?: string;
  startTime?: string;
};

@Component({
  selector: 'app-orbit-confirmation',
  imports: [
    ButtonComponent,
    TelephoneLinkComponent,
    ContactHoursComponent,
    VulnerableCustomersComponent,
    CommonModule,
    QMBlockDirective,
    ManualSignpostingComponent,
    MaintenanceBannerComponent,
    QMEncryptDirective,
    CustomRoundPipe,
    FirstLetterCapitalisePipe,
  ],
  templateUrl: './orbit-confirmation.component.html',
  styleUrl: './orbit-confirmation.component.scss',
  providers: [TimeFormatPipe],
  encapsulation: ViewEncapsulation.None,
})
export class OrbitConfirmationComponent implements OnInit {
  private viewModel = computed(() => {
    const { bundle, mandatoryData } = this.orbitFormClaimService.state.claimData;
    const engineerDetails = this.orbitFormClaimService.storedValue['book-engineer'];
    const customerDetails = this.orbitFormClaimService.storedValue['confirm-your-details'];
    const bookingData = this.orbitFormClaimService.bookingData();
    const appointment = {
      allDay: engineerDetails?.selectedSlot?.allDay,
      date: engineerDetails?.selectedSlot?.date,
      endTime: engineerDetails?.selectedSlot?.endTime,
      startTime: engineerDetails?.selectedSlot?.startTime,
    };
    const vcFeatureFlag = FeatureFlags.getFeatureFlag('vc-capture');

    const isGameReplacement = this.orbitFormClaimService.isGameReplacement;
    const isCollection = bookingData?.serviceOption === 'Courier';
    const isSelfSend = bookingData?.serviceOption === 'Self Send';
    const isPayAndClaim = bookingData?.serviceOption === 'Pay and Claim';
    const isHomeRepair = bookingData?.serviceOption === 'Home Repair';
    const isSPBooking = bookingData?.isSP;
    const isTVRemoteReplacement = bookingData?.isTVRemoteReplacement === true;
    const isTVReplacement =
      isTVRemoteReplacement ||
      (bookingData?.claimQuestionsArray?.some(
        (q: any) =>
          q.answerLabel?.toLowerCase().includes('send a replacement') &&
          q.answerValue?.toLowerCase().includes('send a replacement')
      ) ??
        false);

    const excessRequiredAndPaid = bookingData?.excessAmount !== '0.00' && bookingData?.excessPaid === 'Yes';
    const isService = bundle?.claimType === 'SERVICE';
    const claimTypeWord = isService ? 'service' : 'repair';

    const serviceOrRepairLabels = isService
      ? {
          bookingBoxTitle: 'Your annual service',
          repairerBoxTitle: "Who'll service it",
          repairerLabel: 'Service provider',
        }
      : { bookingBoxTitle: 'Your repair', repairerBoxTitle: "Who'll repair it", repairerLabel: 'Repairer' };

    const tvReplacementLabels = {
      bookingBoxTitle: 'Your replacement',
      repairerBoxTitle: "Who'll send your part",
      repairerLabel: 'Parts supplier',
    };

    const claimTypeLabels = isTVReplacement ? tvReplacementLabels : serviceOrRepairLabels;

    return {
      isVokera:
        (bookingData?.serviceProviderCompanyName || bookingData?.serviceProviderName)?.toLowerCase() === 'vokera',
      isGadget: bundle.isGadget,
      isAppleProduct: bundle.isAppleProduct,
      isFloorCareProduct: bundle.isFloorCareProduct,
      vcFeatureFlag: vcFeatureFlag && !isPayAndClaim,
      confirmedBookingLoaded: !!bookingData,
      mmrLink: `${AppInfo.Environment.claimsURL}/my-plans/${bundle?.planNumber}/claims`,
      bookingOEM: mandatoryData?.bookingOEM?.toLowerCase(),
      manufacturer: bundle?.manufacturer,
      productType: bundle?.productType,
      email: customerDetails?.email,
      isHeatingProduct: bundle?.goodsColour === 'H',
      phoneNumber: customerDetails?.mobile || customerDetails?.landline,
      serviceProvider: bookingData?.serviceProviderCompanyName || bookingData?.serviceProviderName,
      serviceProviderEmail: bookingData?.serviceProviderEmail || bookingData?.serviceProviderPublicEmail,
      serviceProviderContactHours: bookingData?.serviceProviderHours || bookingData?.serviceProviderHoursArray,
      serviceProviderTelephone: bookingData?.serviceProviderTelephone || bookingData?.serviceProviderPhone,
      modelNumber: mandatoryData?.modelNumber,
      planNumber: bundle?.planNumber,
      isManualReferral: bookingData?.manualReferral,
      isGameReplacement,
      isCollection,
      isSelfSend,
      isPayAndClaim,
      isTV: bundle?.isTV,
      manualReferralOption: bookingData?.serviceOption === 'Manual Referral',
      isSPBooking,
      isTVRemoteReplacement,
      isTVReplacement,
      isHomeRepair,
      isService,
      claimTypeWord,
      ...claimTypeLabels,
      nextSteps: this.getNextStepStatements(
        isCollection,
        isSelfSend,
        bundle.isAppleProduct,
        bundle.isFloorCareProduct,
        isTVReplacement,
        isSPBooking,
        isGameReplacement,
        bundle.goodsColour === 'H',
        claimTypeWord
      ),
      showPaymentBox: !!this.orbitFormClaimService.storedValue['payment'] || excessRequiredAndPaid,
      paymentDetails: this.orbitFormClaimService.storedValue['payment'] ?? {
        type: bookingData?.paymentData?.[0]?.type,
        amount: bookingData?.paymentData?.[0]?.amount,
      },
      appointment,
      address: [
        ...[bundle?.addressLine1, bundle?.addressLine2, bundle?.addressLine3, bundle?.addressLine4].map((i) =>
          i?.toLowerCase()
        ),
        bundle?.postalCode,
      ].filter((v) => !!v && v !== ''),
      postCode: bundle?.postalCode,
      bookingSummaryItems: [
        ...(isTVReplacement ? [{ label: 'Part', value: 'TV remote control', testId: 'part-name' }] : []),
        {
          label: 'Engineer visit',
          value: this.formattedAppointmentDate(appointment),
          testId: 'visit-date',
        },
        ...(isCollection
          ? [
              {
                label: 'Collection date',
                value: this.formattedAppointmentDate({
                  allDay: true,
                  date: bookingData?.appointmentDate,
                  endTime: '',
                  startTime: '',
                }),
                testId: 'collection-date',
              },
            ]
          : []),
        {
          label: isCollection ? 'Your product' : 'Your appliance',
          value: `${bundle?.manufacturer} ${bundle?.productType?.toLowerCase()}`,
          testId: 'appliance-name',
        },
        ...(isService
          ? []
          : [
              {
                label: `What's wrong`,
                value: capitaliseText(bookingData?.faultSelected),
                testId: 'fault-name',
              },
            ]),
        {
          label: 'Model number',
          value: mandatoryData?.modelNumber || bookingData?.modelNumber,
          testId: 'model-number',
        },
        {
          label: 'Plan number',
          value: bundle?.planNumber,
          testId: 'plan-number',
          encrypt: true,
        },
        {
          label: 'Reference',
          value: !bookingData?.isSP
            ? [
                bookingData?.claimReference,
                bookingData?.serviceProviderBookingRef,
                bundle?.claimId,
                bookingData?.claimID,
              ].filter((v: string) => !!v && v !== '')[0]
            : [bookingData?.spClaimReference ?? bookingData?.claimID].filter((v: string) => !!v && v !== '')[0],
          testId: 'reference-number',
          encrypt: true,
        },
      ].filter((i) => i.value && i.value !== ''),
      appointmentTime:
        appointment?.startTime && appointment?.endTime
          ? `${this.timeFormatPipe.transform(appointment?.startTime)}-${this.timeFormatPipe.transform(appointment?.endTime)}`
          : '',
    };
  });

  get vm() {
    return this.viewModel();
  }

  get confirmationGAPageType(): ConfirmationPageType {
    if (this.vm.isManualReferral || this.vm.manualReferralOption) return 'call-back';
    if (this.vm.isCollection) return 'collection';
    if (this.vm.isSelfSend) return 'self-send';
    if (this.vm.isPayAndClaim) return 'pay-and-claim';
    if (this.vm.isSPBooking) return 'call-service-provider';
    return 'engineer';
  }

  get excessClaimsMessage(): ExcessClaimsMessage {
    const paymentType = this.vm.paymentDetails?.type?.toString().toLowerCase();

    if (!this.vm.showPaymentBox || !paymentType) return 'NO';
    if (paymentType.includes('call')) return 'CALLOUT';

    return paymentType.includes('excess') ? 'EXCESS' : 'NO';
  }

  get excessAmount(): number {
    return Number(this.vm.paymentDetails?.amount ?? 0);
  }

  get title(): string {
    if (this.vm.isFloorCareProduct) {
      return 'Your repair is approved';
    }
    if (this.vm.isManualReferral || this.vm.manualReferralOption) {
      return 'Call to book your visit';
    }
    if (this.vm.isTVReplacement) {
      return 'Your replacement is approved';
    }
    if (this.vm.isSPBooking) {
      return this.vm.isService ? 'Your service is approved' : 'Your repair is approved';
    }
    if (this.vm.isCollection) {
      return 'Your collection is booked';
    }
    if (this.vm.isSelfSend) {
      return 'Sending us your product';
    }
    if (this.vm.isPayAndClaim) {
      return 'Repair and claim approved';
    }
    return 'Thanks for booking';
  }

  constructor(
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly timeFormatPipe: TimeFormatPipe,
    private readonly gaService: GAService
  ) {}

  ngOnInit() {
    // LiveChat.hideLiveChatLauncher();
    const bundleValue = sessionStorage.getItem(ORBIT_BUNDLE);
    const bundleKey = sessionStorage.getItem(ORBIT_BUNDLE_SESSION_KEY);

    if (bundleValue) {
      if (!this.orbitFormClaimService.state.claimData) {
        this.orbitFormClaimService.handleLoadedInData(JSON.parse(bundleValue));
      }
    }

    if (bundleKey) {
      this.orbitFormClaimService.loadBundle(bundleKey!, false);
    }

    this.gaService.sendGATag({
      ...this.gaService.getConfirmationGATags(this.confirmationGAPageType).PAGE_LOAD(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      excessAmount: this.excessAmount,
      excessClaimsMessage: this.excessClaimsMessage,
      repairer: this.vm?.serviceProvider || '',
    });
    this.gaService.sendGATag({
      ...this.gaService.getConfirmationGATags(this.confirmationGAPageType).CONFIRMATION_EVENT(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      excessAmount: this.excessAmount,
      excessClaimsMessage: this.excessClaimsMessage,
      repairer: this.vm?.serviceProvider || '',
    });

    Tracking.capture('booking_confirmed', {
      appointment: this.vm?.appointment,
    });
  }

  clearConfirmedBooking(): void {
    sessionStorage.removeItem('confirmedBooking');
  }

  formattedAppointmentDate(appointment: Appointment): string | undefined {
    if (!appointment?.date) return;
    const apptDate = format(appointment.date, 'do MMMM yyyy');
    const dayOfWeek = format(new Date(appointment.date), 'EEEE');
    if (appointment?.allDay || (!appointment?.startTime && !appointment?.endTime)) {
      return `${dayOfWeek} ${apptDate}`;
    }
    return `${dayOfWeek} ${apptDate} between ${this.timeFormatPipe.transform(appointment?.startTime)} - ${this.timeFormatPipe.transform(appointment?.endTime)}`;
  }

  getNextStepStatements(
    isCollection: boolean,
    isSelfSend: boolean,
    isAppleProduct: boolean,
    isFloorCareProduct: boolean,
    isTVReplacement: boolean = false,
    isSPBooking: boolean,
    isGameReplacement: boolean,
    isHeating: boolean,
    claimTypeWord: string = 'repair'
  ) {
    if (isTVReplacement) {
      return [
        {
          title: 'Supplier update',
          icon: 'fa-envelope',
          color: '#CE7E07',
          description: 'Within 24 hours of booking, the supplier will contact you by WhatsApp, phone or email.',
        },
        {
          title: 'Send a photo',
          icon: 'fa-camera',
          color: '#09788C',
          description: 'They might ask for a picture of the model and serial number label.',
        },
        {
          title: 'Getting your part',
          icon: 'fa-truck',
          color: '#8B56E7',
          description: "They'll order the part and keep you updated on delivery and availability.",
        },
      ];
    }
    if (isFloorCareProduct) {
      const bookingData = this.orbitFormClaimService.bookingData();
      const provider = bookingData?.serviceProviderCompanyName || bookingData?.serviceProviderName;
      if (provider?.toLowerCase() === FloorcareProviders.NORTH_WEST_APPLIANCE) {
        return [
          {
            title: 'You’ll get a call',
            icon: 'fa-phone',
            color: '#8B56E7',
            description: 'The repairer will call within one working day (if you booked before 3pm).',
          },
          {
            title: 'Delivery for repair',
            icon: 'fa-truck',
            color: '#09788C',
            description:
              'A courier will bring packaging and collect your item. (We use APT for upright vacuums and DPD for handheld or cylinder.)',
          },
          {
            title: 'Repair updates',
            icon: 'fa-envelope',
            color: '#CE7E07',
            description: 'The repairer will tell you when they receive your item, and send you repair updates.',
          },
        ];
      }
      return [
        {
          title: 'You’ll get a call',
          icon: 'fa-phone',
          color: '#8B56E7',
          description: 'The repairer will call within one working day (if you booked before 3pm).',
        },
        {
          title: 'Parts or collection',
          icon: 'fa-comments-question-check',
          color: '#09788C',
          description:
            'The repairer will ask about the fault. They might send a replacement part or arrange a collection with you.',
        },
        {
          title: 'Delivery for repair',
          icon: 'fa-truck',
          color: '#CE7E07',
          description:
            'If you arrange collection, a DPD courier will bring the packaging for your item and deliver it to the repairer.',
        },
        {
          title: 'Repair updates',
          icon: 'fa-envelope',
          color: '#FC4CD0',
          description: 'The repairer will tell you when they receive your item, and send you repair updates.',
        },
      ];
    }
    if (isSelfSend) {
      return [
        {
          title: 'Take it off iCloud',
          icon: 'fa-cloud',
          color: '#4F5061',
          removeIfNotApple: true,
          description:
            "Please remove your device from Find My iPhone by <a target='_blank' href='https://support.apple.com/en-gb/guide/icloud/mmfc0eeddd/icloud'>following Apple's instructions.</a>",
        },
        {
          title: 'Get your device ready',
          icon: 'fa-sim-cards',
          color: '#8B56E7',
          description: 'Remove your password, back up data and remove any memory or SIM cards.',
        },
        {
          title: 'Packaging and labelling',
          icon: 'fa-envelope-open-text',
          color: '#09788C',
          description: 'We’ll post you the packaging, Freepost label and return note that you need.',
        },
        {
          title: 'Prepare the package',
          icon: 'fa-box',
          color: '#CE7E07',
          description: 'Put the return label inside the package and attach the Freepost label to the front.',
        },
        {
          title: 'Post it to us',
          icon: 'fa-envelope',
          color: '#FC4CD0',
          description: 'Send the package to us. Once received, we’ll keep you updated on the repair.',
        },
      ].filter((i) => {
        if (isAppleProduct) return true;
        return !i.removeIfNotApple;
      });
    }

    if (isCollection) {
      return [
        {
          title: 'Time slot confirmation',
          icon: 'fa-clock',
          color: '#09788C',
          description: 'On the day, you’ll get an email or text message with your collection time slot.',
        },
        {
          title: 'Get your device ready',
          icon: 'fa-sim-cards',
          color: '#8B56E7',
          description: 'Remove your password, back up data and remove any memory or SIM cards.',
        },
        {
          title: 'Take it off iCloud',
          icon: 'fa-cloud',
          color: '#4F5061',
          removeIfNotApple: true,
          description:
            "Please remove your device from Find My iPhone by <a target='_blank' href='https://support.apple.com/en-gb/guide/icloud/mmfc0eeddd/icloud'>following Apple's instructions.</a>",
        },
        {
          title: 'Packaging and labelling',
          icon: 'fa-box',
          color: '#CE7E07',
          description: 'The DPD courier will bring the packaging and postage labels for your item.',
        },
        {
          title: 'Repair updates',
          icon: 'fa-envelope',
          color: '#FC4CD0',
          removeIfGameReplacement: true,
          description: 'The repairer will tell you when they receive your item, and send you repair updates.',
        },
      ].filter((i) => {
        if (isAppleProduct) return true;
        if (isGameReplacement) return !i.removeIfNotApple && !i.removeIfGameReplacement;
        return !i.removeIfNotApple;
      });
    }

    if (isSPBooking) {
      return [
        {
          title: 'Arrange parking',
          icon: 'fa-truck',
          color: '#8b56e7',
          description: 'The engineer will need safe parking near your home – have a permit ready (if needed).',
        },
        {
          title: 'Provide access',
          icon: 'fa-universal-access',
          color: '#09788c',
          description: `Make sure there’s easy access and enough space for the ${claimTypeWord}.`,
        },
        {
          title: claimTypeWord === 'service' ? 'Service updates' : 'Repair updates',
          icon: 'fa-envelope',
          color: '#ce7e07',
          description: `You’ll get a booking confirmation by email, and text reminders before the ${claimTypeWord}.`,
        },
      ];
    }

    return [
      {
        title: 'Arrange parking',
        icon: 'fa-truck',
        color: '#8b56e7',
        description: 'The engineer will need safe parking near your home – have a permit ready (if needed).',
      },
      {
        title: 'Provide access',
        icon: 'fa-universal-access',
        color: '#09788c',
        description: isHeating
          ? `Make sure there’s easy access and enough space for the ${claimTypeWord}.`
          : "Before the repair, unplug your appliance and make it easy to reach. (Also defrost it, if it's a freezer.)",
      },
      {
        title: claimTypeWord === 'service' ? 'Service updates' : 'Repair updates',
        icon: 'fa-envelope',
        color: '#ce7e07',
        description: `You’ll get a booking confirmation by email, and text reminders before the ${claimTypeWord}.`,
      },
    ];
  }

  handleDownload(): void {
    downloadPDF();
  }

  trackClick() {
    this.gaService.sendGATag({
      ...this.gaService.getConfirmationGATags(this.confirmationGAPageType).MMR(),
      claimedTimeSlot: this.vm?.appointmentTime || '',
      excessAmount: this.excessAmount,
      excessClaimsMessage: this.excessClaimsMessage,
      repairer: this.vm?.serviceProvider || '',
    });
  }

  get collectionBannerMessage(): string {
    if (this.vm.isFloorCareProduct) {
      const message =
        floorcareBannerMessage[this.vm.serviceProvider.toLowerCase() as keyof typeof floorcareBannerMessage];
      if (message) return message;
      return 'The repairer will call you to arrange a collection.';
    }
    return "We've arranged for a DPD courier to collect your item";
  }

  get mmrMessage(): string {
    if (this.vm.isFloorCareProduct) {
      if (this.vm.serviceProvider?.toLowerCase() === FloorcareProviders.NORTH_WEST_APPLIANCE) {
        return 'View your collection and repair details';
      }
      return 'View your repair details';
    }
    if (this.vm.isManualReferral) {
      return 'Once you’ve booked a date, you can check when your engineer will arrive and change your details.';
    }
    if (this.vm.isSelfSend) {
      return 'View or change your repair details';
    }
    if (this.vm.isCollection) {
      return 'View or change your collection and repair details';
    }
    return 'You can check when your engineer will arrive and change your details.';
  }
}
